import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { QrCode, Printer, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartLabels() {
  const [parts, setParts] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [qrData, setQrData] = useState(null);
  const [barcodeData, setBarcodeData] = useState(null);
  const [allLabels, setAllLabels] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchParts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/parts`);
      setParts(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchParts(); }, [fetchParts]);

  const generateQR = async (part) => {
    setSelectedPart(part);
    try {
      const [qr, bc] = await Promise.all([
        axios.get(`${API}/parts/${part.id}/qrcode`),
        axios.get(`${API}/parts/${part.id}/barcode`),
      ]);
      setQrData(qr.data);
      setBarcodeData(bc.data);
    } catch (e) { toast.error("Erro ao gerar codigo"); }
  };

  const generateAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/parts/labels/all`);
      setAllLabels(res.data);
    } catch (e) { toast.error("Erro ao gerar etiquetas"); }
    finally { setLoading(false); }
  };

  const printLabels = () => {
    const win = window.open("", "_blank");
    // For single label, use full data. For all labels, use qr only (no barcode in batch)
    const isSingle = allLabels.length === 0 && qrData;
    
    let labelsHtml = "";
    if (isSingle && qrData && barcodeData) {
      labelsHtml = `
        <div class="label">
          <div class="label-header">Etiqueta: ${qrData.code}</div>
          <div class="label-body">
            <div class="label-left">
              <div class="section-title">QR Code</div>
              <div class="qr-box"><img src="${qrData.qr_image}" /></div>
              <div class="qr-text">${qrData.qr_data}</div>
            </div>
            <div class="label-right">
              <div class="info-box">
                <div class="part-code">${qrData.code}</div>
                <div class="part-desc">${qrData.description}</div>
                <div class="part-details">Material: ${selectedPart?.plastic_type || "-"}</div>
                <div class="part-details">Peso: ${selectedPart?.unit_weight || "-"}g</div>
                <div class="part-details">Cliente: ${selectedPart?.client || "-"}</div>
              </div>
              <div class="barcode-section">
                <div class="section-title">Codigo de Barras</div>
                <div class="barcode-box"><img src="${barcodeData.barcode_image}" /></div>
              </div>
            </div>
          </div>
        </div>`;
    } else {
      const items = allLabels.length > 0 ? allLabels : (qrData ? [qrData] : []);
      labelsHtml = items.map(l => `
        <div class="label-small">
          <div class="label-header-sm">${l.code}</div>
          <div class="label-body-sm">
            <img src="${l.qr_image}" class="qr-sm" />
            <div class="info-sm">
              <div class="code-sm">${l.code}</div>
              <div class="desc-sm">${l.description}</div>
            </div>
          </div>
        </div>`).join("");
    }

    win.document.write(`<!DOCTYPE html><html><head><title>Etiquetas EP3D</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;padding:20px;margin:0}
        /* Single label - full size */
        .label{border:2px solid #e0e0e0;border-radius:12px;padding:20px;max-width:600px;margin:0 auto;page-break-after:always}
        .label-header{font-size:18px;font-weight:700;color:#333;margin-bottom:16px}
        .label-body{display:flex;gap:20px}
        .label-left{flex:0 0 220px;text-align:center}
        .label-right{flex:1}
        .section-title{font-size:13px;font-weight:600;color:#555;margin-bottom:8px;font-style:italic}
        .qr-box{border:1px solid #e8e8e8;border-radius:10px;padding:12px;display:inline-block;background:#fff}
        .qr-box img{width:180px;height:180px}
        .qr-text{font-size:9px;color:#999;margin-top:8px;word-break:break-all;max-width:220px}
        .info-box{background:#f8f8f8;border-radius:10px;padding:16px;margin-bottom:16px}
        .part-code{font-size:28px;font-weight:800;color:#111}
        .part-desc{font-size:16px;font-weight:600;color:#333;margin-top:2px}
        .part-details{font-size:14px;color:#666;margin-top:6px}
        .barcode-section{text-align:center}
        .barcode-box{border:1px solid #e8e8e8;border-radius:10px;padding:12px;display:inline-block;background:#fff}
        .barcode-box img{height:70px}
        /* Grid of small labels */
        .grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}
        .label-small{border:1.5px solid #ddd;border-radius:10px;padding:12px;page-break-inside:avoid}
        .label-header-sm{font-size:10px;color:#888;font-weight:600;margin-bottom:6px}
        .label-body-sm{display:flex;gap:10px;align-items:center}
        .qr-sm{width:80px;height:80px}
        .info-sm{flex:1}
        .code-sm{font-size:16px;font-weight:800;color:#111}
        .desc-sm{font-size:11px;color:#555;margin-top:2px}
        @media print{
          body{padding:10px}
          .label{border:1.5px solid #ccc;box-shadow:none}
          .grid{grid-template-columns:repeat(3,1fr);gap:8px}
          .label-small{border:1px solid #ccc}
        }
      </style></head><body>
      ${isSingle ? labelsHtml : `<div class="grid">${labelsHtml}</div>`}
    </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  return (
    <div className="space-y-6" data-testid="part-labels-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Etiquetas QR / Barcode</h1>
          <p className="text-gray-500 mt-1">Gerar e imprimir codigos para identificacao de pecas</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={generateAll} disabled={loading}>
            <QrCode className="h-4 w-4 mr-2" />{loading ? "A gerar..." : "Gerar Todas"}
          </Button>
          <Button onClick={printLabels} disabled={!qrData && allLabels.length === 0}>
            <Printer className="h-4 w-4 mr-2" />Imprimir Etiquetas
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Parts list */}
        <div className="col-span-1">
          <Card>
            <CardHeader><CardTitle>Pecas ({parts.length})</CardTitle></CardHeader>
            <CardContent className="max-h-[600px] overflow-y-auto space-y-1">
              {parts.map(p => (
                <button key={p.id} onClick={() => generateQR(p)} className={`w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-blue-50 transition ${selectedPart?.id === p.id ? 'bg-blue-100 border border-blue-300' : 'border border-transparent'}`}>
                  <span className="font-bold">{p.code}</span> - {p.description}
                </button>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* QR/Barcode preview */}
        <div className="col-span-2">
          {selectedPart && qrData ? (
            <Card>
              <CardHeader><CardTitle>Etiqueta: {selectedPart.code}</CardTitle></CardHeader>
              <CardContent>
                <div className="flex gap-8 items-start">
                  {/* QR Code */}
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-600 mb-2">QR Code</p>
                    <img src={qrData.qr_image} alt="QR Code" className="w-48 h-48 border rounded-lg p-2" />
                    <p className="text-xs text-gray-400 mt-2 max-w-[200px] break-all">{qrData.qr_data}</p>
                  </div>
                  {/* Barcode */}
                  {barcodeData && (
                    <div className="text-center">
                      <p className="text-sm font-semibold text-gray-600 mb-2">Codigo de Barras</p>
                      <img src={barcodeData.barcode_image} alt="Barcode" className="h-24 border rounded-lg p-2" />
                    </div>
                  )}
                  {/* Info */}
                  <div className="flex-1">
                    <div className="bg-slate-50 rounded-lg p-4 border">
                      <h3 className="font-bold text-xl text-gray-900">{selectedPart.code}</h3>
                      <p className="text-gray-600">{selectedPart.description}</p>
                      <p className="text-sm text-gray-400 mt-2">Material: {selectedPart.plastic_type || "-"}</p>
                      <p className="text-sm text-gray-400">Peso: {selectedPart.unit_weight || "-"}g</p>
                      <p className="text-sm text-gray-400">Cliente: {selectedPart.client || "-"}</p>
                    </div>
                    <Button className="mt-4 w-full" onClick={printLabels}>
                      <Printer className="h-4 w-4 mr-2" />Imprimir Etiqueta
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : allLabels.length > 0 ? (
            <Card>
              <CardHeader><CardTitle>Todas as Etiquetas ({allLabels.length})</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-3">
                  {allLabels.map((l, i) => (
                    <div key={i} className="border rounded-lg p-3 text-center">
                      <img src={l.qr_image} alt={l.code} className="w-20 h-20 mx-auto" />
                      <p className="font-bold text-sm mt-1">{l.code}</p>
                      <p className="text-xs text-gray-500 truncate">{l.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-20 text-center">
                <QrCode className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 font-medium">Selecione uma peca para gerar o codigo</p>
                <p className="text-gray-400 text-sm mt-1">Ou clique "Gerar Todas" para criar etiquetas de todas as pecas</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
